/**
 * Счётчики статистики 2FA: сколько введено секретов и получено кодов.
 * Код считается один раз на пару «секрет × окно TOTP» — после ввода секрета,
 * открытия постоянной ссылки или копирования кода из списка. Обновление кода
 * по таймеру не считается. Наружу уходят только числа; отпечатки секретов
 * живут лишь в памяти вкладки и никуда не сохраняются.
 */
export interface CodeSource {
  secret: string;
  period?: number;
}

type Sender = (payload: { units?: number; codes?: number }, keepalive: boolean) => void;

const FLUSH_DELAY_MS = 1500;
const MAX_REMEMBERED = 5000;

let sender: Sender | null = null;
let pendingSecrets = 0;
let pendingCodes = 0;
let timer: number | null = null;
const counted = new Set<string>();

// FNV-1a: короткий необратимый отпечаток для дедупликации в памяти.
function fingerprint(secret: string): string {
  let hash = 0x811c9dc5;
  for (let i = 0; i < secret.length; i += 1) {
    hash ^= secret.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193) >>> 0;
  }
  return hash.toString(36);
}

function flush(keepalive = false) {
  if (timer !== null) {
    window.clearTimeout(timer);
    timer = null;
  }
  if (!sender || (!pendingSecrets && !pendingCodes)) return;
  const payload: { units?: number; codes?: number } = {};
  if (pendingSecrets) payload.units = Math.min(pendingSecrets, 5000);
  if (pendingCodes) payload.codes = Math.min(pendingCodes, 5000);
  pendingSecrets = 0;
  pendingCodes = 0;
  try {
    sender(payload, keepalive);
  } catch {
    // Статистика не должна мешать работе страницы.
  }
}

function schedule() {
  if (timer === null) timer = window.setTimeout(() => flush(false), FLUSH_DELAY_MS);
}

export function configureCodeTelemetry(send: Sender): () => void {
  sender = send;
  const onHide = () => { if (document.visibilityState === 'hidden') flush(true); };
  document.addEventListener('visibilitychange', onHide);
  return () => {
    document.removeEventListener('visibilitychange', onHide);
    flush(true);
    sender = null;
  };
}

export function noteSecretsEntered(units: number) {
  if (!Number.isInteger(units) || units < 1) return;
  pendingSecrets += units;
  schedule();
}

/** Возвращает, сколько кодов засчитано впервые. */
export function noteCodesObtained(items: CodeSource[], now = Date.now()): number {
  let added = 0;
  for (const item of items) {
    if (!item || typeof item.secret !== 'string' || !item.secret) continue;
    const period = Number.isInteger(item.period) && (item.period as number) > 0 ? (item.period as number) : 30;
    const key = `${fingerprint(item.secret)}:${period}:${Math.floor(now / 1000 / period)}`;
    if (counted.has(key)) continue;
    if (counted.size >= MAX_REMEMBERED) counted.clear();
    counted.add(key);
    added += 1;
  }
  if (added) {
    pendingCodes += added;
    schedule();
  }
  return added;
}

/** Только для тестов. */
export function resetCodeTelemetry() {
  counted.clear();
  pendingSecrets = 0;
  pendingCodes = 0;
  if (timer !== null) window.clearTimeout(timer);
  timer = null;
  sender = null;
}

export function flushCodeTelemetry() {
  flush(false);
}
